import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchFromStrapi } from "../api/strapi";
import { ServiceDetailsSkeleton, EmptyState, ErrorState, SkeletonBlock } from "../components/SkeletonUI";
import DOMPurify from 'dompurify';

export default function ServiceDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [useCases, setUseCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [useCasesLoading, setUseCasesLoading] = useState(false);
  const [error, setError] = useState(null);


  const heroRef = useRef(null);

  /* ======================
     FETCH SERVICE DETAILS
     ====================== */
  useEffect(() => {
    async function fetchServiceDetails() {
      try {
        setLoading(true);
        const res = await fetchFromStrapi(
          `/ecl-services-sections/${id}`
        );
        const data = res?.data?.data || res?.data;
        setService(data);
        console.log('Fetched service details:', data);
      } catch (err) {
        console.error(err);
        setError("Failed to load service details");
      } finally {
        setLoading(false);
      }
    }

    fetchServiceDetails();
  }, [id]);

  

  /* ======================
     FETCH LINKED CASE STUDIES
     ====================== */
  useEffect(() => {
    if (!service) return;

    const serviceTitle = service?.attributes?.Title;
    console.log('title for fetching use cases:', serviceTitle);
    if (!serviceTitle) return;

    async function fetchUseCases() {
      try {
        setUseCasesLoading(true);

        const res = await fetchFromStrapi(
          `/ecl-case-study-sections?filters[serviceType][$eq]=${encodeURIComponent(
            serviceTitle
          )}`
        );

        const data = res?.data?.data || [];
        setUseCases(data);
        console.log('Fetched use cases:', data);
      } catch (err) {
        console.error(err);
        setError("Failed to load use cases");
      } finally {
        setUseCasesLoading(false);
      }
    }

    fetchUseCases();
  }, [service]);



  /* ======================
     STATES
     ====================== */
  if (loading) return <ServiceDetailsSkeleton />;

  if (error || !service) {
    return (
      <div className="container">
        <ErrorState
          message={error || "This service couldn't be found."}
          onBack={() => navigate(-1)}
        />
      </div>
    );
  }

  const { attributes } = service;
  const sanitizedHtmlDescription = DOMPurify.sanitize(attributes?.Description || "");
  console.log('Sanitized HTML description:', sanitizedHtmlDescription);

  return (
    <main className="container">

      {/* BACK BUTTON */}
      <div className="mb-4">
        <button
          className="btn btn-outline-dark"
          onClick={() => navigate(-1)}
        >
          Back
        </button>
      </div>

      {/* HERO */}
      <section
        ref={heroRef}
        style={{
          position: "relative",
          marginBottom: 80,
          height: 520,
          backgroundImage: `url(${attributes?.Image?.data?.attributes?.url})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
        }}
      >
        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)" }} />
        <div style={{ position: "relative", zIndex: 1, padding: "0 8%" }}>
          <div className="hero-info-card">
            <p
              className="text-uppercase fw-semibold mb-2"
              style={{ fontSize: "0.75rem", letterSpacing: "0.1em", color: "#009EDC" }}
            >
              ECL Solutions
            </p>
            <h1 className="fw-bold mb-3" style={{ fontSize: "2rem", lineHeight: 1.2, color: "#0a1a3a" }}>
              {attributes?.Title}
            </h1>
            {/* <p className="mb-0" style={{ color: "#4B5563", lineHeight: 1.7 }}>{attributes?.Description}</p> */}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section style={{ marginBottom: 120 }}>
        <div 
          className="service-stats-grid"
        >
          <p style={{fontSize:'18px'}}><div dangerouslySetInnerHTML={{ __html: sanitizedHtmlDescription }} /></p>
         
        </div>
      </section>

      {/* CASE STUDIES */}
      <section style={{ marginBottom: 120 }}>
        <h2 className="fw-bold mb-4">
          {attributes?.Title} use cases
        </h2>

        {useCasesLoading && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {[0, 1].map(i => (
              <div
                key={i}
                className="use-case-skeleton-grid"
              >
                <div style={{ padding: 28 }}>
                  <SkeletonBlock width={90} height={12} style={{ marginBottom: 14 }} />
                  <SkeletonBlock height={22} style={{ marginBottom: 10 }} />
                  <SkeletonBlock width="70%" height={22} style={{ marginBottom: 20 }} />
                  <SkeletonBlock width={80} height={14} />
                </div>
                <SkeletonBlock height={220} radius={0} />
              </div>
            ))}
          </div>
        )}

        {!useCasesLoading && useCases.length === 0 && (
          <EmptyState
            title="No case studies yet"
            description={`We're building case studies for ${attributes?.Title}. Check back soon.`}
          />
        )}

        <div style={{ display: "grid", gap: 32 }}>
          {useCases.map(cs => {
            const csAttr = cs.attributes;

            return (
              <article
                key={cs.id}
                onClick={() => navigate(`/case-studies/${cs.id}`)}
                className="use-case-card-grid"
                style={{
                  background: "#f5f9ff",
                  cursor: "pointer"
                }}
              >
                <div style={{ padding: 32 }}>
                  <small>{csAttr?.serviceType}</small>
                  <h4 className="fw-semibold mt-2">
                    {csAttr?.Title}
                  </h4>
                  <span className="text-decoration-underline">
                    Learn more
                  </span>
                </div>

                <div>
                  <img
                    src={csAttr?.CoverImage?.data?.attributes?.url}
                    alt={csAttr?.Title}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover"
                    }}
                  />
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* TRENDING / BLOG PLACEHOLDER */}
      <section style={{ marginBottom: 120 }}>
        {/* <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="fw-bold">What’s trending</h2>
          <span
            className="text-decoration-underline"
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/blogs")}
          >
            View all
          </span>
        </div>

        {blogsLoading && <p>Loading articles…</p>}

        {!blogsLoading && blogs.length === 0 && (
          <p>No articles related to this service yet.</p>
        )} */}

        {/* <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 24
          }}
        >
          {blogs.slice(0, 4).map(blog => {
            const blogAttr = blog.attributes;
            const image =
              blogAttr?.image?.data?.attributes?.url;

            return (
              <article
                key={blog.id}
                onClick={() => navigate(`/blogs/${blog.id}`)}
                style={{
                  borderRadius: 14,
                  background: "#f5f9ff",
                  overflow: "hidden",
                  cursor: "pointer"
                }}
              >
                <div
                  style={{
                    height: 160,
                    backgroundImage: `url(${image})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center"
                  }}
                />

                <div style={{ padding: 16 }}>
                  <small className="text-muted">
                    {blogAttr?.serviceType}
                  </small>
                  <h6 className="fw-semibold mt-2">
                    {blogAttr?.Title}
                  </h6>
                </div>
              </article>
            );
          })}
        </div> */}
      </section>

      {/* <section style={{ marginBottom: 120 }}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="fw-bold">What’s trending</h2>
          <span className="text-decoration-underline">View all</span>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 24
          }}
        >
          {[1, 2, 3, 4].map(i => (
            <div
              key={i}
              style={{
                height: 260,
                borderRadius: 14,
                background: "#e5e5e5"
              }}
            />
          ))}
        </div>
      </section> */}

    </main>
  );
}
